import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { logoutSession, type SessionUser } from './auth';

/**
 * The signed-in session, as the envelope described it.
 *
 * Tokens live in `sessionStorage`, not `localStorage`. This build has no
 * session hardening — no rotation on a used refresh token, no device binding —
 * so the only thing reducing the blast radius is scope, and session scope ends
 * when the tab does. `localStorage` would outlive the browser and leave a
 * usable refresh token on a shared machine.
 */
export interface Session {
  access: string;
  refresh: string;
  user: SessionUser;
}

const SESSION_KEY = 'escrowbuild:session';

/** Storage is best-effort: private mode can refuse it, and a refused write must
 *  not take the sign-in down with it. */
function readStoredSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    // All three are required: a stored value that has the user but not the
    // tokens is a session-shaped object the app would then render as signed in,
    // and `access` is typed as a string that would not actually be there.
    return parsed?.user && parsed?.access && parsed?.refresh ? parsed : null;
  } catch {
    return null;
  }
}

export type SessionStatus = 'authenticated' | 'unauthenticated';

export interface SessionValue {
  status: SessionStatus;
  user: SessionUser | null;
  /** Stores a completed sign-in. */
  grant: (access: string, refresh: string, user: SessionUser) => void;
  /** Clears the session locally, with no network call. */
  clear: () => void;
  /** Ends the session server-side and clears locally — always. */
  signOut: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within <SessionProvider>');
  return context;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  /*
     Read synchronously from `sessionStorage` rather than in an effect.

     The store is local and the read is cheap, so there is no round trip to
     await and no second render to reach the signed-in header — an effect here
     would paint the signed-out shell first and flip it, so every reload of a
     signed-in tab would flash the login screen.

     ponytail: no refresh-on-boot, because nothing in the app calls an
     authenticated endpoint yet. When the first one lands, refresh the stored
     access token at this seam and keep the status behind it.
  */
  const [session, setSession] = useState<Session | null>(readStoredSession);

  const clear = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // Best-effort; the in-memory state below is what the app renders from.
    }
    setSession(null);
  }, []);

  const grant = useCallback((access: string, refresh: string, user: SessionUser) => {
    const next: Session = { access, refresh, user };
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    } catch {
      // Best-effort, as above: the session still works for this page's lifetime.
    }
    setSession(next);
  }, []);

  const signOut = useCallback(() => {
    const refresh = session?.refresh;

    /*
       The local clear happens FIRST, and is not awaited.

       Signing out is two things — the local session ending, and the refresh
       token being blacklisted — and neither may be able to fail the other.
       Awaiting the request (even inside a `finally`) keeps the session alive
       for as long as it takes, and a request to a host that accepts the
       connection and then never replies never settles at all, which would trap
       the visitor inside the session they just ended. So the intent is honoured
       now and the blacklist is best-effort behind it.
    */
    clear();
    if (refresh) {
      logoutSession(refresh).catch(() => {
        // The token may live on server-side. Nothing here can fix that, and the
        // visitor has already left.
      });
    }
  }, [session, clear]);

  const value = useMemo<SessionValue>(
    () => ({
      status: session ? 'authenticated' : 'unauthenticated',
      user: session?.user ?? null,
      grant,
      clear,
      signOut,
    }),
    [session, grant, clear, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
